import type { BookingSession } from '@/features/bookings-management/types';

import type { CustomChatChannel } from '../types';
import {
  compareCustomChatChannelsByStableKey,
  getCustomChatChannelActivityTime,
} from './channelOrdering';

export type CustomChatJobSupportGroupId =
  | 'needs-response'
  | 'today'
  | 'tomorrow'
  | 'overdue'
  | 'upcoming'
  | 'completed'
  | 'archived';

export type CustomChatJobSupportGroup = {
  defaultCollapsed?: boolean;
  id: CustomChatJobSupportGroupId;
  label: string;
  sort: 'activity-desc' | 'date-asc' | 'date-desc';
};

export type GroupedCustomChatJobSupportChannel = {
  channel: CustomChatChannel;
  hasDate: boolean;
  sortTime: number;
};

const ARCHIVE_AFTER_DAYS = 2;
const TERMINAL_STATUSES = [
  'completed',
  'done',
  'finished',
  'cancelled',
  'canceled',
];

const DATE_KEYS = ['bookingDate', 'date'];
const TIME_KEYS = ['bookingTime', 'time'];
const STATUS_KEYS = [
  'bookingSessionStatus',
  'booking_session_status',
  'sessionStatus',
  'session_status',
  'bookingStatus',
  'booking_status',
  'status',
  'statusValue',
  'statusDisplay',
];
const SESSION_ID_KEYS = [
  'bookingSessionId',
  'sessionId',
  'booking_session_id',
  'id',
];
const ARCHIVED_KEYS = [
  'isArchived',
  'is_archived',
  'archivedAt',
  'archived_at',
];

export const CUSTOM_CHAT_JOB_SUPPORT_GROUPS: CustomChatJobSupportGroup[] = [
  { id: 'needs-response', label: 'Needs response', sort: 'activity-desc' },
  { id: 'today', label: 'Today', sort: 'date-asc' },
  {
    id: 'tomorrow',
    label: 'Tomorrow',
    sort: 'date-asc',
    defaultCollapsed: true,
  },
  { id: 'overdue', label: 'Overdue', sort: 'date-desc' },
  {
    id: 'upcoming',
    label: 'Upcoming',
    sort: 'date-asc',
    defaultCollapsed: true,
  },
  {
    id: 'completed',
    label: 'Completed',
    sort: 'date-desc',
    defaultCollapsed: true,
  },
  {
    id: 'archived',
    label: 'Archived',
    sort: 'date-desc',
    defaultCollapsed: true,
  },
];

const getRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

const coercePositiveNumber = (value: unknown): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const getMetadataValue = (
  metadata: Record<string, unknown>,
  keys: string[]
): unknown => {
  for (const key of keys) {
    const value = metadata[key];
    if (value !== undefined && value !== null && String(value).trim()) {
      return value;
    }
  }

  return null;
};

const getMetadataNumber = (
  metadata: Record<string, unknown>,
  keys: string[]
): number | null => {
  for (const key of keys) {
    const parsed = coercePositiveNumber(metadata[key]);
    if (parsed) return parsed;
  }

  return null;
};

const normalizeStatus = (value: unknown): string => {
  const record = getRecord(value);
  const rawValue =
    record.value ?? record.display ?? record.name ?? record.status ?? value;

  return String(rawValue ?? '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ');
};

const isTerminalStatus = (
  metadata: Record<string, unknown>,
  session?: BookingSession
): boolean => {
  const statusValue =
    getMetadataValue(metadata, STATUS_KEYS) ?? session?.booking_session_status;
  const normalizedStatus = normalizeStatus(statusValue);

  return TERMINAL_STATUSES.some((status) => normalizedStatus.includes(status));
};

const hasStatus = (metadata: Record<string, unknown>) =>
  Boolean(getMetadataValue(metadata, STATUS_KEYS));

const isArchivedChannel = (channel: CustomChatChannel): boolean => {
  const metadata = channel.metadata ?? {};
  return Boolean(
    channel.archived_at ||
      getMetadataValue(metadata, ARCHIVED_KEYS) ||
      getMetadataValue(metadata, ['hiddenAt', 'hidden_at'])
  );
};

const parseDateCandidate = (value: unknown): Date | null => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const text = String(value).trim();

  if (text.includes('T')) {
    const date = new Date(text);
    if (!Number.isNaN(date.getTime())) return date;
  }

  const numericDateMatch = text.match(/^(\d{1,4})[/-](\d{1,2})[/-](\d{1,4})/);

  if (numericDateMatch) {
    const [, firstValue, secondValue, thirdValue] = numericDateMatch;
    const firstNumber = Number(firstValue);
    const secondNumber = Number(secondValue);
    const thirdNumber = Number(thirdValue);
    const isYearFirst = firstValue.length === 4;
    const year = isYearFirst ? firstNumber : thirdNumber;
    const month = secondNumber;
    const day = isYearFirst ? thirdNumber : firstNumber;
    const date = new Date(year, month - 1, day);

    if (
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
    ) {
      return date;
    }
  }

  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? null : date;
};

const applyTimeToDate = (date: Date, timeValue: unknown): Date => {
  if (!timeValue) return date;

  const timeText = String(timeValue).trim();
  const match = timeText.match(/^(\d{1,2}):(\d{2})\s*([ap]m)?$/i);
  if (!match) return date;

  const [, hourValue, minuteValue, meridiemValue] = match;
  const meridiem = meridiemValue?.toLowerCase();
  let hours = Number(hourValue);
  const minutes = Number(minuteValue);

  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return date;
  if (meridiem === 'pm' && hours < 12) hours += 12;
  if (meridiem === 'am' && hours === 12) hours = 0;

  const dateWithTime = new Date(date);
  dateWithTime.setHours(hours, minutes, 0, 0);
  return dateWithTime;
};

const startOfLocalDay = (date: Date): Date => {
  const day = new Date(date);
  day.setHours(0, 0, 0, 0);
  return day;
};

const getDaysFromToday = (date: Date): number => {
  const today = startOfLocalDay(new Date());
  const targetDate = startOfLocalDay(date);
  return Math.round(
    (targetDate.getTime() - today.getTime()) / (24 * 60 * 60 * 1000)
  );
};

const getSessionDate = (
  channel: CustomChatChannel,
  session?: BookingSession
): { date: Date | null; hasDate: boolean; sortTime: number } => {
  const metadata = channel.metadata ?? {};
  const dateValue = getMetadataValue(metadata, DATE_KEYS) ?? session?.date;
  const timeValue = getMetadataValue(metadata, TIME_KEYS) ?? session?.time;
  const parsedDate = parseDateCandidate(dateValue);

  if (!parsedDate) {
    return { date: null, hasDate: false, sortTime: Number.MAX_SAFE_INTEGER };
  }

  const date = applyTimeToDate(parsedDate, timeValue);
  return { date, hasDate: true, sortTime: date.getTime() };
};

export const resolveCustomChatJobSupportSessionId = (
  channel?: CustomChatChannel | null
): number | null => {
  if (!channel || channel.type !== 'jobSupport') return null;

  return (
    getMetadataNumber(channel.metadata ?? {}, SESSION_ID_KEYS) ??
    coercePositiveNumber(channel.booking_session_id)
  );
};

export const getCustomChatJobSupportSessionIdsToFetch = (
  channels: CustomChatChannel[]
) =>
  channels.reduce<number[]>((sessionIds, channel) => {
    if (channel.type !== 'jobSupport' || isArchivedChannel(channel)) {
      return sessionIds;
    }

    const metadata = channel.metadata ?? {};
    const hasDate = Boolean(getMetadataValue(metadata, DATE_KEYS));

    if (hasDate && hasStatus(metadata)) return sessionIds;

    const sessionId = resolveCustomChatJobSupportSessionId(channel);
    if (sessionId && !sessionIds.includes(sessionId)) {
      sessionIds.push(sessionId);
    }

    return sessionIds;
  }, []);

export const getCustomChatJobSupportGroupId = (
  channel: CustomChatChannel,
  sessionsById: Map<number, BookingSession>
): CustomChatJobSupportGroupId => {
  const metadata = channel.metadata ?? {};
  const sessionId = resolveCustomChatJobSupportSessionId(channel);
  const session = sessionId ? sessionsById.get(sessionId) : undefined;
  const { date } = getSessionDate(channel, session);

  if (isArchivedChannel(channel)) return 'archived';
  if (Number(channel.unread_count ?? 0) > 0) return 'needs-response';
  if (!date) return 'upcoming';

  const daysFromToday = getDaysFromToday(date);

  if (isTerminalStatus(metadata, session)) {
    return daysFromToday < -ARCHIVE_AFTER_DAYS ? 'archived' : 'completed';
  }

  if (daysFromToday === 0) return 'today';
  if (daysFromToday === 1) return 'tomorrow';
  if (daysFromToday < 0) return 'overdue';

  return 'upcoming';
};

export const groupCustomChatJobSupportChannels = (
  channels: CustomChatChannel[],
  sessionsById: Map<number, BookingSession>
) => {
  const groupedChannels = CUSTOM_CHAT_JOB_SUPPORT_GROUPS.reduce<
    Record<CustomChatJobSupportGroupId, GroupedCustomChatJobSupportChannel[]>
  >(
    (groups, group) => ({
      ...groups,
      [group.id]: [],
    }),
    {} as Record<
      CustomChatJobSupportGroupId,
      GroupedCustomChatJobSupportChannel[]
    >
  );

  channels.forEach((channel) => {
    const sessionId = resolveCustomChatJobSupportSessionId(channel);
    const session = sessionId ? sessionsById.get(sessionId) : undefined;
    const { hasDate, sortTime } = getSessionDate(channel, session);
    const groupId = getCustomChatJobSupportGroupId(channel, sessionsById);

    groupedChannels[groupId].push({ channel, hasDate, sortTime });
  });

  return groupedChannels;
};

export const sortCustomChatJobSupportGroupItems = (
  items: GroupedCustomChatJobSupportChannel[],
  sort: CustomChatJobSupportGroup['sort']
) =>
  [...items].sort((firstItem, secondItem) => {
    if (sort === 'activity-desc') {
      const activityDiff =
        getCustomChatChannelActivityTime(secondItem.channel) -
        getCustomChatChannelActivityTime(firstItem.channel);

      if (activityDiff) return activityDiff;
      return compareCustomChatChannelsByStableKey(
        firstItem.channel,
        secondItem.channel
      );
    }

    if (firstItem.hasDate !== secondItem.hasDate) {
      return firstItem.hasDate ? -1 : 1;
    }

    const dateDiff =
      sort === 'date-desc'
        ? secondItem.sortTime - firstItem.sortTime
        : firstItem.sortTime - secondItem.sortTime;

    if (dateDiff) return dateDiff;
    return compareCustomChatChannelsByStableKey(
      firstItem.channel,
      secondItem.channel
    );
  });
